// client/src/pages/ResetPasswordPage.js
// Public screen reached from the emailed reset link (/reset-password?token=...).
// Consumes the token and sets a new password.
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const ResetPasswordPage = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';

    const [newPassword, setNewPassword] = React.useState('');
    const [confirm, setConfirm] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [done, setDone] = React.useState(false);
    const [error, setError] = React.useState('');

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword.length < 12) return setError('New password must be at least 12 characters.');
        if (newPassword !== confirm) return setError('Passwords do not match.');

        setBusy(true);
        try {
            const resp = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Reset failed.');
            setDone(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const inputCls = 'block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 sm:text-sm';

    return (
        <div className="flex items-center justify-center min-h-screen bg-slate-100">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 text-center">Choose a new password</h2>

                {!token ? (
                    <p className="text-sm text-red-600 text-center">
                        This link is missing its reset token. Please use the link from your email, or request a new one.
                    </p>
                ) : done ? (
                    <div className="space-y-4">
                        <p className="text-green-700 bg-green-50 border border-green-200 rounded p-3 text-sm">
                            Your password has been reset. You can now sign in with your new password.
                        </p>
                        <Link to="/portal" className="block text-center text-sm font-medium text-indigo-600 hover:underline">
                            Go to sign in
                        </Link>
                    </div>
                ) : (
                    <form className="space-y-4" onSubmit={submit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                            <input type="password" autoComplete="new-password" required autoFocus className={inputCls}
                                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                            <p className="text-xs text-gray-500 mt-1">At least 12 characters.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                            <input type="password" autoComplete="new-password" required className={inputCls}
                                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                        </div>
                        {error && <p className="text-sm text-red-600">{error}</p>}
                        <button type="submit" disabled={busy}
                            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:bg-indigo-400">
                            {busy ? 'Saving…' : 'Reset password'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPasswordPage;
